import React, { useEffect, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { useCurrentInternalUser, isAdmin, notifyCurrentUserChanged } from '../lib/currentUser';

// Sidebar context selector (Part A5 optional slot) — the shared
// "label + select" control that sits just below the lockup, matching
// Quotezilla's CLIENT VIEW. Here it drives the dashboard scope
// ("Just Me" vs "Everyone"), replacing the old header dropdown so the
// control lives in the sidebar family slot.
//
// Admin/super_user only. Persists to internal_users.dashboard_scope and
// calls notifyCurrentUserChanged() so the Dashboard (which now reads the
// scope straight off currentInternalUser) re-fetches with the new lens.
export default function SidebarContextSelector() {
  const { user } = useCurrentInternalUser();
  const [value, setValue] = useState('mine');

  // Mirror the persisted preference; optimistic local state keeps the
  // control snappy while the write + refetch settle.
  useEffect(() => {
    setValue(user?.dashboard_scope === 'everyone' ? 'everyone' : 'mine');
  }, [user?.dashboard_scope]);

  if (!isAdmin(user)) return null;

  const onChange = async (e) => {
    const next = e.target.value;
    setValue(next);
    if (!user?.id) return;
    await supabase.from('internal_users').update({ dashboard_scope: next }).eq('id', user.id);
    notifyCurrentUserChanged();
  };

  return (
    // Hidden rather than shrunk: a scope selector whose label and value are
    // both cut down says nothing, and picking the wrong scope silently changes
    // what every page reports. It returns in full at xl.
    <div className="hidden xl:block px-4 pb-4 pt-3 flex-shrink-0 border-b border-sb-line">
      <label className="block">
        <span className="block font-['Metropolis'] text-[10px] font-semibold tracking-[0.14em] uppercase text-sb-section mb-1.5">
          View
        </span>
        <div className="relative">
          <select
            value={value}
            onChange={onChange}
            aria-label="Dashboard view scope"
            className="appearance-none w-full bg-sb-hover border border-sb-line rounded-lg h-9 pl-3 pr-9 font-['Metropolis'] text-sm font-semibold text-sb-text hover:border-sb-guide focus:outline-none focus-visible:border-fusion-green transition-colors cursor-pointer">
            <option value="mine">Just Me</option>
            <option value="everyone">Everyone</option>
          </select>
          <ChevronDown size={16} strokeWidth={2} aria-hidden="true" className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sb-muted" />
        </div>
      </label>
    </div>
  );
}
